import React, { useMemo } from "react";
import { ReloadOutlined } from "@ant-design/icons";

import { EditAnswer } from "components/bid_module/requirement_summary_report_section/EditAnswer";
import Divider from "components/ui/divider/Divider";
import Spinner from "components/ui/spinner/Spinner";
import { useGenerateSingleAnswer } from "lib/hooks/api/bid/bid_question/useGenerateSingleAnswer";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { EventData, EventNames } from "lib/tracking";
import { BidReportType, inferEnumType } from "lib/types/graphQLEnums";
import {
  BidReportAllAnswers_Result_Questions,
  BidReportEntry_Answer_Sources,
} from "../../../lib/generated/app-service-gql/graphql";
import { Text } from "../../../styles/utility-components";
import DocumentSources from "./DocumentSources";
import { toInlineSources } from "./utils";

import css from "./QuestionAndAnswer.module.scss";

type Props = {
  question: BidReportAllAnswers_Result_Questions;
  bidReportId: string;
  reportType: BidReportType;
  debugMode: boolean;
  onSelectDocument: (
    source: BidReportEntry_Answer_Sources,
    trackingContext: EventNames,
    trackingData: EventData,
  ) => Promise<void>;
  showDivider: boolean;
  slideOutOpen: boolean;
  noticeTitle: string | undefined;
  noticeId?: string;
  hasEditAccess: boolean;
  hasQuestionRequalify: boolean;
  isStreaming?: boolean;
};

export function QuestionAndAnswer({
  question,
  bidReportId,
  reportType,
  debugMode,
  onSelectDocument,
  showDivider,
  slideOutOpen,
  noticeTitle,
  noticeId,
  hasEditAccess,
  hasQuestionRequalify,
  isStreaming,
}: Props) {
  // Regenerating a single answer is a Stotles-admin tool, not a customer-facing one.
  const { data: currentUser } = useCurrentUser();
  const allowRequalify = currentUser?.isAdmin === true && hasQuestionRequalify;
  const { mutateAsync: generateSingleAnswerMutation, isLoading: generateSingleAnswerIsLoading } =
    useGenerateSingleAnswer();

  const inlineSources = useMemo(
    () =>
      question.sources && question.sources.length > 0
        ? toInlineSources(question.sources, noticeId)
        : undefined,
    [question.sources, noticeId],
  );

  return (
    <>
      <article
        key={question.title}
        className={css.questionAndAns}
        style={{ gridTemplateColumns: `${slideOutOpen ? 200 : 400}px max-content` }}
      >
        <Text className={css.questionTitle}>
          {question.title}
          {allowRequalify && (
            <>
              {generateSingleAnswerIsLoading ? (
                <Spinner className={css.icon} color="currentColor" label="Regenerating answer" />
              ) : (
                <ReloadOutlined
                  className={css.icon}
                  onClick={async () => {
                    await generateSingleAnswerMutation({
                      input: {
                        question: question.title,
                        bidReportId,
                        reportType: inferEnumType(reportType),
                      },
                    });
                  }}
                />
              )}
            </>
          )}
        </Text>

        <aside className={css.answerContainer}>
          <EditAnswer
            answerValue={question.answer?.answer ?? ""}
            answerId={question.answerId}
            autoQualify={question.autoQualify}
            hasEditAccess={hasEditAccess}
            isStreaming={isStreaming}
            inlineSources={inlineSources}
          />
          {!inlineSources && question.sources && question.sources.length > 0 && (
            <span className={css.sources}>
              <DocumentSources
                sources={question.sources}
                onSelectDocument={(source) =>
                  onSelectDocument(source, EventNames.citationOpened, {
                    "Action type": "Link clicked",
                    "Criteria name": question.title,
                  })
                }
                noticeId={noticeId}
                noticeTitle={noticeTitle}
              />
              {debugMode && <pre>{JSON.stringify(question.sources, null, 2)}</pre>}
            </span>
          )}
        </aside>
      </article>
      {showDivider && <Divider className={css.divider} />}
    </>
  );
}
